import React from 'react';
import { HashRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from './lib/query/queryClient';
import ThemeProvider from './components/ThemeProvider';
import ErrorBoundary from './components/ErrorBoundary';
import AppLayout from './components/AppLayout';
import ContactsPage from './features/contacts/components/ContactsPage';
import OfferingsPage from './features/offerings/components/OfferingsPage';
import OfferingCategoriesPage from './features/offering-categories/components/OfferingCategoriesPage';
import CustomerSegmentsPage from './features/settings/customer-segments/components/CustomerSegmentsPage';
import ContactCategoriesPage from './features/settings/contact-categories/components/ContactCategoriesPage';
import LeadPipelineStagesPage from './features/settings/lead-pipeline-stages/components/LeadPipelineStagesPage';
import LeadsPage from './features/leads/components/LeadsPage';
import PipelinePage from './features/leads/components/PipelinePage';
import CampaignsPage from './features/campaigns/components/CampaignsPage';
import DocumentsPage from './features/document-management/components/DocumentsPage';
import KundaliPage from './features/contacts/components/KundaliPage';
import BulkOperationsRegistryPage from './features/bulk-operations/components/BulkOperationsRegistryPage';
import UserManagementPage from './features/users/components/UserManagementPage';
import RolesPage from './features/roles/components/RolesPage';
import ForgotPasswordPage from './features/auth/components/ForgotPasswordPage';
import SetPasswordPage from './features/auth/components/SetPasswordPage';
import ProfilePage from './features/auth/components/ProfilePage';
import ProtectedRoute from './features/auth/components/ProtectedRoute';
import { PermissionsProvider } from './features/permissions/PermissionsContext';
import RequirePermission from './features/permissions/RequirePermission';
import UnauthorizedPage from './features/permissions/UnauthorizedPage';
import TasksPage from './features/tasks/components/TasksPage';
import TaskDashboardPage from './features/tasks/components/TaskDashboardPage';
import DailyTaskTrackingPage from './features/tasks/components/DailyTaskTrackingPage';
import CommissionCalculatorPage from './features/commissions/components/CommissionCalculatorPage';
import CommissionReportsPage from './features/commissions/components/CommissionReportsPage';
import HostConsolePage from './features/host/components/HostConsolePage';
import MarketingHomePage from './features/marketing/components/MarketingHomePage';
import MarketingScrollRedirect from './features/marketing/components/MarketingScrollRedirect';
import LoginRoutePage from './features/auth/components/LoginRoutePage';
import RequestAccessPage from './features/marketing/components/RequestAccessPage';
import { appPath, LEGACY_APP_ROUTE_REDIRECTS } from './routes/paths';
import {
  LegacyKundaliRedirect,
  LegacyLeadsDetailRedirect,
  AppSettingsToSetupRedirect,
  LegacySettingsRedirect,
} from './routes/LegacyAppRedirect';
import AppIndexRoute from './routes/AppIndexRoute';
import SetupIndexRedirect from './routes/SetupIndexRedirect';

const queryClient = createQueryClient();

function PermRoute({
  permission,
  children,
}: {
  permission: string;
  children: React.ReactNode;
}) {
  return <RequirePermission permission={permission}>{children}</RequirePermission>;
}

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HashRouter>
          <Routes>
              <Route path="/" element={<MarketingHomePage />} />
              <Route path="features" element={<MarketingScrollRedirect scrollTo="features" />} />
              <Route path="commitments" element={<MarketingScrollRedirect scrollTo="commitments" />} />
              <Route path="pricing" element={<MarketingScrollRedirect scrollTo="pricing" />} />
              <Route path="signup" element={<RequestAccessPage />} />
              <Route path="login" element={<LoginRoutePage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="set-password" element={<SetPasswordPage />} />

              {LEGACY_APP_ROUTE_REDIRECTS.map(({ from, to }) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
              <Route path="leads/:id" element={<LegacyLeadsDetailRedirect />} />
              <Route path="kundali/:id" element={<LegacyKundaliRedirect />} />
              <Route path="settings/*" element={<LegacySettingsRedirect />} />
              <Route path="setup/*" element={<LegacySettingsRedirect />} />

              <Route
                path="app"
                element={
                  <ProtectedRoute>
                    <PermissionsProvider>
                      <ErrorBoundary>
                        <Outlet />
                      </ErrorBoundary>
                    </PermissionsProvider>
                  </ProtectedRoute>
                }
              >
                <Route element={<AppLayout />}>
                  <Route index element={<AppIndexRoute />} />
                  <Route
                    path="contacts"
                    element={
                      <PermRoute permission="contacts.view">
                        <ContactsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="documents"
                    element={
                      <PermRoute permission="quotes.view">
                        <DocumentsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="offerings"
                    element={
                      <PermRoute permission="offerings.view">
                        <OfferingsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="offerings/categories"
                    element={
                      <PermRoute permission="offering-categories.view">
                        <OfferingCategoriesPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="leads/pipeline"
                    element={
                      <PermRoute permission="leads.view">
                        <PipelinePage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="leads/:id"
                    element={
                      <PermRoute permission="leads.view">
                        <LeadsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="leads"
                    element={
                      <PermRoute permission="leads.view">
                        <LeadsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="campaigns"
                    element={
                      <PermRoute permission="campaigns.view">
                        <CampaignsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="daily-task-tracking"
                    element={
                      <PermRoute permission="tasks.dashboard.view">
                        <DailyTaskTrackingPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="task-dashboard"
                    element={
                      <PermRoute permission="tasks.dashboard.view">
                        <TaskDashboardPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="tasks"
                    element={
                      <PermRoute permission="tasks.view">
                        <TasksPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="commissions/calculator"
                    element={
                      <PermRoute permission="commissions.view">
                        <CommissionCalculatorPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="commissions/reports"
                    element={
                      <PermRoute permission="commissions.view">
                        <CommissionReportsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="admin/users"
                    element={
                      <PermRoute permission="users.view">
                        <UserManagementPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="admin/roles"
                    element={
                      <PermRoute permission="roles.view">
                        <RolesPage />
                      </PermRoute>
                    }
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route
                    path="kundali/:id"
                    element={
                      <PermRoute permission="contacts.view">
                        <KundaliPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="host"
                    element={
                      <PermRoute permission="host.tenants.view">
                        <HostConsolePage />
                      </PermRoute>
                    }
                  />
                  <Route path="setup" element={<SetupIndexRedirect />} />
                  <Route
                    path="setup/customer-segments"
                    element={
                      <PermRoute permission="customer-segments.view">
                        <CustomerSegmentsPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="setup/contact-categories"
                    element={
                      <PermRoute permission="contact-categories.view">
                        <ContactCategoriesPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="setup/lead-pipeline-stages"
                    element={
                      <PermRoute permission="lead-pipeline-stages.view">
                        <LeadPipelineStagesPage />
                      </PermRoute>
                    }
                  />
                  <Route
                    path="bulk-operations"
                    element={
                      <PermRoute permission="bulk-operations.view">
                        <BulkOperationsRegistryPage />
                      </PermRoute>
                    }
                  />
                  <Route path="unauthorized" element={<UnauthorizedPage />} />
                  <Route path="*" element={<Navigate to={appPath()} replace />} />
                </Route>
                <Route path="settings/*" element={<AppSettingsToSetupRedirect />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
